import { useEffect, useMemo, useState } from 'react';
import { IconAlertCircle, IconLock, IconCheck, IconArrowDown, IconArrowUp } from '@tabler/icons-react';
// (IconArrowUp used in delta preview when override is upward)
import Modal from './ui/Modal';

/**
 * PriceOverrideModal — Phase 2.6b.
 *
 * Single-screen flow: new price + reason + supervisor PIN, applied in
 * one POST to /api/price-overrides/authorise. No detour modals, no
 * chained PIN sheet. On success the parent updates the cart line; the
 * resulting override token travels with the order POST so the
 * server can validate + audit it on placement.
 *
 * Approval gate: anyone SUPERVISOR / MANAGER / ADMIN / OWNER may
 * approve (server enforces). Cashier cannot self-approve.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  lineId: string;
  productId: string | null;
  productName: string;
  variantSize?: string;
  currentUnitPriceCents: number;
  originalUnitPriceCents: number;     // price before any prior override on this line — usually = current
  /** Called on successful authorise. The parent stores the token on the cart line. */
  onAuthorised: (result: {
    lineId: string;
    newUnitPriceCents: number;
    originalUnitPriceCents: number;
    reason: string;
    approvedBy: { id: string; name: string; role: string };
    token: string;
  }) => void;
  /** Called when supervisor wants to revert an existing override on this line. */
  onRemove?: (lineId: string) => void;
  hasExistingOverride?: boolean;
}

const REASON_OPTIONS = [
  'Menu shows different price',
  'Promo / signage mismatch',
  'Quoted to customer',
  'Damaged goods discount',
  'Other',
] as const;

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function PriceOverrideModal({
  open, onClose, lineId, productId, productName, variantSize,
  currentUnitPriceCents, originalUnitPriceCents,
  onAuthorised, onRemove, hasExistingOverride,
}: Props) {
  const [priceInput, setPriceInput] = useState<string>('');
  const [reasonChoice, setReasonChoice] = useState<typeof REASON_OPTIONS[number]>(REASON_OPTIONS[0]);
  const [reasonOther, setReasonOther] = useState<string>('');
  const [pin, setPin] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Pre-fill with the current price as a starting point.
      setPriceInput((currentUnitPriceCents / 100).toFixed(2));
      setReasonChoice(REASON_OPTIONS[0]);
      setReasonOther('');
      setPin('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, currentUnitPriceCents]);

  const newCents = useMemo(() => {
    const v = Number(priceInput.replace(',', '.'));
    if (!isFinite(v) || v < 0) return null;
    return Math.round(v * 100);
  }, [priceInput]);

  const deltaCents = newCents == null ? 0 : newCents - originalUnitPriceCents;
  const isDownward = deltaCents < 0;

  const finalReason = reasonChoice === 'Other' ? reasonOther.trim() : reasonChoice;
  const canSubmit =
    !submitting
    && newCents != null
    && newCents !== originalUnitPriceCents
    && finalReason.length > 0
    && /^\d{4}$/.test(pin);

  async function submit() {
    if (!canSubmit || newCents == null) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await fetch('/api/price-overrides/authorise', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          productName,
          originalUnitPriceCents,
          newUnitPriceCents: newCents,
          reason: finalReason,
          pin,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(body.error ?? 'Could not authorise the override. Try again.');
        setSubmitting(false);
        return;
      }
      onAuthorised({
        lineId,
        newUnitPriceCents: newCents,
        originalUnitPriceCents,
        reason: finalReason,
        approvedBy: body.approvedBy,
        token: body.token,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Network error');
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} size="lg" title="Override line price">
      {/* Product context */}
      <div className="rounded-lg bg-light border border-border p-4 mb-4">
        <p className="text-12 font-bold uppercase tracking-wider text-gray-700 mb-1">Item</p>
        <p className="text-16 font-bold text-heading">
          {productName}
          {variantSize ? <span className="text-gray-700 font-semibold"> · {variantSize}</span> : null}
        </p>
        <div className="flex items-center justify-between mt-3">
          <p className="text-12 font-bold uppercase tracking-wider text-gray-700">Current price</p>
          <p className="text-18 font-bold text-heading tabular-nums">{fmtR(originalUnitPriceCents)}</p>
        </div>
      </div>

      {/* New price */}
      <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
        New unit price (R) <span className="text-danger">*</span>
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={priceInput}
        onChange={(e) => setPriceInput(e.target.value.replace(/[^0-9.,]/g, ''))}
        placeholder="0.00"
        autoFocus
        className="w-full h-14 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-22 font-bold tabular-nums text-heading mb-3"
      />

      {/* Delta preview */}
      {newCents != null && newCents !== originalUnitPriceCents && (
        <div className={[
          'rounded-lg border p-3 mb-4 flex items-center justify-between',
          isDownward ? 'bg-success-soft border-success/30' : 'bg-warning-soft border-warning/40',
        ].join(' ')}>
          <div className="flex items-center gap-2">
            {isDownward ? <IconArrowDown size={18} className="text-success" /> : <IconArrowUp size={18} className="text-warning" />}
            <p className={`text-13 font-bold uppercase tracking-wider ${isDownward ? 'text-success' : 'text-warning'}`}>
              {isDownward ? 'Override down' : 'Override up'}
            </p>
          </div>
          <p className={`text-16 font-bold tabular-nums ${isDownward ? 'text-success' : 'text-warning'}`}>
            {isDownward ? '−' : '+'}{fmtR(Math.abs(deltaCents))}
          </p>
        </div>
      )}

      {/* Reason */}
      <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
        Reason <span className="text-danger">*</span>
      </label>
      <div className="grid grid-cols-1 gap-1.5 mb-3">
        {REASON_OPTIONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReasonChoice(r)}
            className={[
              'h-11 px-3 rounded-md border-2 text-13 font-bold text-left transition-colors flex items-center gap-2',
              reasonChoice === r
                ? 'bg-primary text-[#FFFFFF] border-primary'
                : 'bg-surface border-border text-heading hover:border-primary',
            ].join(' ')}
          >
            {reasonChoice === r && <IconCheck size={16} stroke={3} />}
            {r}
          </button>
        ))}
      </div>
      {reasonChoice === 'Other' && (
        <input
          type="text"
          value={reasonOther}
          onChange={(e) => setReasonOther(e.target.value)}
          placeholder="Type the reason"
          className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-15 font-semibold text-heading mb-3"
        />
      )}

      {/* PIN */}
      <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
        Supervisor / Manager PIN <span className="text-danger">*</span>
      </label>
      <div className="flex items-center gap-3 mb-2">
        <IconLock size={18} className="text-gray-500 flex-shrink-0" />
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="••••"
          className="flex-1 h-14 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-22 font-bold tabular-nums text-heading text-center tracking-[0.5em]"
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(); }}
        />
      </div>
      <p className="text-12 text-gray-700 mb-4">Any supervisor, manager or owner can approve. The cashier cannot approve their own override.</p>

      {error && (
        <div className="mb-4 rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        {hasExistingOverride && onRemove && (
          <button
            type="button"
            onClick={() => { onRemove(lineId); onClose(); }}
            disabled={submitting}
            className="flex-1 h-12 rounded-md bg-surface border-2 border-danger text-14 font-bold text-danger hover:bg-danger-soft disabled:opacity-50 transition-colors"
          >
            Revert to menu price
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-[2] h-12 rounded-md bg-primary text-[#FFFFFF] text-15 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          {submitting ? 'Authorising…' : 'Apply override'}
        </button>
      </div>
    </Modal>
  );
}
